import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { renderArticleIframeUrlEmbed, type CropAreaPercent, type SlideContent } from 'lorena-templates'
import { pb } from '../../lib/pocketbase'
import { Timeline } from './components/Timeline'
import { TemplatePickerModal } from './components/TemplatePickerModal'
import { SlideForm } from './components/SlideForm'
import { SlidePreviewFrame } from './components/SlidePreviewFrame'
import { StatusSelect } from './components/StatusSelect'
import { slideToCtx } from './lib/slideCtx'
import { downloadSlidesAsZip } from './lib/downloadZip'
import { absoluteRenderServiceUrl, captureMateriaPngs } from '../../lib/renderService'
import { resolveFileUrl } from '../../lib/files'
import type { MateriaStatus } from './lib/status'
import type { SlideDraft } from './types'

interface MateriaRecord {
  id: string
  title: string
  slug: string
  status: MateriaStatus
}

interface SlideRecord {
  id: string
  materia: string
  template_id: string
  position: number
  content: SlideContent
  image?: string
  image_original?: string
  image_transform?: SlideDraft['imageTransform']
  updated?: string
}

function toDraft(record: SlideRecord): SlideDraft {
  return {
    id: record.id,
    materiaId: record.materia,
    templateId: record.template_id,
    position: record.position,
    content: record.content ?? {},
    imageFileId: record.image || undefined,
    originalImageFileId: record.image_original || undefined,
    imageTransform: record.image_transform ?? null,
    updated: record.updated,
  }
}

type PickerState = { mode: 'add' } | { mode: 'change'; slideId: string } | null

export function MateriaEditorPage() {
  const { materiaId } = useParams<{ materiaId: string }>()
  const [materia, setMateria] = useState<MateriaRecord | null>(null)
  const [slides, setSlides] = useState<SlideDraft[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [picker, setPicker] = useState<PickerState>(null)
  const [loading, setLoading] = useState(true)
  const [copyFeedback, setCopyFeedback] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [downloadingZip, setDownloadingZip] = useState(false)
  const [downloadError, setDownloadError] = useState('')
  // Um timer por slide (não um só compartilhado) — trocar de tela rapidamente
  // enquanto uma edição ainda está no "debounce" não pode cancelar/perder o
  // save de outra tela.
  const saveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const pendingContentSaves = useRef<Map<string, SlideContent>>(new Map())
  const pendingSaveRequests = useRef<Set<Promise<unknown>>>(new Set())

  useEffect(() => {
    if (!materiaId) return
    pb.collection('materias')
      .getOne<MateriaRecord>(materiaId)
      .then(setMateria)
      .catch(() => setMateria(null))
    pb.collection('slides')
      .getFullList<SlideRecord>({ filter: `materia="${materiaId}"`, sort: 'position' })
      .then((records) => {
        const drafts = records.map(toDraft)
        setSlides(drafts)
        if (drafts.length) setSelectedId(drafts[0].id)
      })
      .finally(() => setLoading(false))
  }, [materiaId])

  const selected = slides.find((s) => s.id === selectedId) ?? null

  const trackSaveRequest = useCallback(<T,>(request: Promise<T>) => {
    let tracked: Promise<T>
    tracked = request.finally(() => pendingSaveRequests.current.delete(tracked))
    pendingSaveRequests.current.add(tracked)
    return tracked
  }, [])

  const persistSlideContent = useCallback((id: string, content: SlideContent) => {
    return trackSaveRequest(pb.collection('slides').update(id, { content }))
  }, [trackSaveRequest])

  const flushSlideContentNow = useCallback((id: string, content: SlideContent) => {
    pendingContentSaves.current.delete(id)
    clearTimeout(saveTimers.current.get(id))
    saveTimers.current.delete(id)
    return persistSlideContent(id, content)
  }, [persistSlideContent])

  const flushPendingSaves = useCallback(async () => {
    const immediateSaves = Array.from(pendingContentSaves.current.entries()).map(([id, content]) => flushSlideContentNow(id, content))
    await Promise.allSettled(immediateSaves)

    while (pendingSaveRequests.current.size) {
      await Promise.allSettled(Array.from(pendingSaveRequests.current))
    }
  }, [flushSlideContentNow])

  const addSlide = async (templateId: string) => {
    if (!materiaId) return
    const nextPosition = slides.length ? Math.max(...slides.map((s) => s.position)) + 1000 : 1000
    const record = await pb.collection('slides').create<SlideRecord>({
      materia: materiaId,
      template_id: templateId,
      position: nextPosition,
      content: {},
    })
    const draft = toDraft(record)
    setSlides((prev) => [...prev, draft])
    setSelectedId(draft.id)
    setPicker(null)
  }

  const changeTemplate = async (slideId: string, templateId: string) => {
    await pb.collection('slides').update(slideId, { template_id: templateId, content: {}, image: null, image_original: null, image_transform: null })
    setSlides((prev) =>
      prev.map((s) => (s.id === slideId ? { ...s, templateId, content: {}, imageFileId: undefined, originalImageFileId: undefined, imageTransform: null } : s)),
    )
    setPicker(null)
  }

  const onPick = (templateId: string) => {
    if (picker?.mode === 'change') changeTemplate(picker.slideId, templateId)
    else addSlide(templateId)
  }

  /** Arrastar-e-soltar: `targetId` null = mover pro final. Insere sempre ANTES do alvo. */
  const reorderSlide = (draggedId: string, targetId: string | null) => {
    if (draggedId === targetId) return
    const sorted = slides.slice().sort((a, b) => a.position - b.position)
    const others = sorted.filter((s) => s.id !== draggedId)
    const dropIndex = targetId ? others.findIndex((s) => s.id === targetId) : others.length
    if (dropIndex === -1) return

    let newPosition: number
    if (others.length === 0) newPosition = 1000
    else if (dropIndex <= 0) newPosition = others[0].position - 1000
    else if (dropIndex >= others.length) newPosition = others[others.length - 1].position + 1000
    else newPosition = (others[dropIndex - 1].position + others[dropIndex].position) / 2

    setSlides((prev) => prev.map((s) => (s.id === draggedId ? { ...s, position: newPosition } : s)))
    trackSaveRequest(pb.collection('slides').update(draggedId, { position: newPosition }))
  }

  const deleteSlide = async (slideId: string) => {
    await pb.collection('slides').delete(slideId)
    setSlides((prev) => {
      const next = prev.filter((s) => s.id !== slideId)
      if (selectedId === slideId) setSelectedId(next[0]?.id ?? null)
      return next
    })
  }

  const updateContent = useCallback((id: string, content: SlideContent) => {
    setSlides((prev) => prev.map((s) => (s.id === id ? { ...s, content } : s)))
    pendingContentSaves.current.set(id, content)
    clearTimeout(saveTimers.current.get(id))
    saveTimers.current.set(
      id,
      setTimeout(() => {
        if (pendingContentSaves.current.get(id) !== content) return
        pendingContentSaves.current.delete(id)
        saveTimers.current.delete(id)
        void persistSlideContent(id, content)
      }, 400),
    )
  }, [persistSlideContent])

  const uploadImage = useCallback(async (slideId: string, file: File, originalFile: File | null = null) => {
    setUploadingImage(true)
    try {
      const record = await pb.collection('slides').update<SlideRecord>(slideId, {
        image: file,
        image_original: originalFile,
        image_transform: null,
      })
      setSlides((prev) => (prev.map((s) => (s.id === slideId ? toDraft(record) : s))))
    } finally {
      setUploadingImage(false)
    }
  }, [])

  const updateTransform = useCallback((slideId: string, area: CropAreaPercent) => {
    setSlides((prev) => prev.map((s) => (s.id === slideId ? { ...s, imageTransform: area, updated: new Date().toISOString() } : s)))
    trackSaveRequest(
      pb.collection('slides').update<SlideRecord>(slideId, { image_transform: area }).then((record) => {
        setSlides((prev) => prev.map((s) => (s.id === slideId ? { ...s, updated: record.updated } : s)))
        return record
      }),
    )
  }, [trackSaveRequest])

  const copyHtml = async () => {
    if (!materiaId) return
    await flushPendingSaves()
    const embedUrl = `${absoluteRenderServiceUrl()}/embed/materia/${encodeURIComponent(materiaId)}`
    const html = renderArticleIframeUrlEmbed(embedUrl, {
      title: materia?.title ?? 'Lorena Magazine',
      slideCount: slides.length,
    })
    await navigator.clipboard.writeText(html)
    setCopyFeedback(true)
    setTimeout(() => setCopyFeedback(false), 2000)
  }

  const changeStatus = async (status: MateriaStatus) => {
    if (!materiaId) return
    setMateria((prev) => (prev ? { ...prev, status } : prev))
    await pb.collection('materias').update(materiaId, { status })
  }

  const downloadStatics = async () => {
    if (!materiaId) return
    setDownloadingZip(true)
    setDownloadError('')
    try {
      await flushPendingSaves()
      const items = await captureMateriaPngs(materiaId)
      await downloadSlidesAsZip(items, materia?.slug ?? materiaId)
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Falha ao gerar os PNGs.')
    } finally {
      setDownloadingZip(false)
    }
  }

  if (loading) return <p style={{ color: 'var(--muted)' }}>Carregando…</p>

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <h1 style={{ fontSize: 18, fontWeight: 600 }}>{materia?.title ?? 'Timeline'}</h1>
          {materia && <StatusSelect value={materia.status} onChange={changeStatus} />}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {downloadError && <span style={{ fontSize: 12, color: '#b3261e' }}>{downloadError}</span>}
          <button type="button" className="secondary" disabled={!slides.length || downloadingZip || uploadingImage} onClick={downloadStatics}>
            {downloadingZip ? 'Gerando…' : 'Baixar estáticos'}
          </button>
          <button type="button" className="secondary" disabled={!slides.length} onClick={copyHtml}>
            {copyFeedback ? 'Copiado ✓' : 'Copiar iframe HTML'}
          </button>
        </div>
      </div>

      <Timeline
        slides={slides.slice().sort((a, b) => a.position - b.position)}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onAddClick={() => setPicker({ mode: 'add' })}
        onDelete={deleteSlide}
        onChangeTemplate={(id) => setPicker({ mode: 'change', slideId: id })}
        onReorder={reorderSlide}
      />

      {selected && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, marginTop: 32, alignItems: 'flex-start' }}>
          <div style={{ flex: '0 0 auto', maxWidth: '100%' }}>
            <SlidePreviewFrame templateId={selected.templateId} content={selected.content} ctx={slideToCtx(selected)} displayWidth={320} />
          </div>
          <div style={{ flex: '1 1 280px', minWidth: 0, maxWidth: 420 }}>
            <SlideForm
              templateId={selected.templateId}
              content={selected.content}
              onChange={(content) => updateContent(selected.id, content)}
              imageUrl={slideToCtx(selected).imageUrl}
              originalImageUrl={selected.originalImageFileId ? resolveFileUrl('slides', selected.id, selected.originalImageFileId, selected.updated) : undefined}
              imageTransform={selected.imageTransform}
              onUploadImage={(file, originalFile) => uploadImage(selected.id, file, originalFile)}
              onTransformChange={(area) => updateTransform(selected.id, area)}
              uploadingImage={uploadingImage}
            />
          </div>
        </div>
      )}

      {picker && <TemplatePickerModal onPick={onPick} onClose={() => setPicker(null)} />}
    </div>
  )
}
